import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Laptop } from "lucide-react";

const conditionStyles = {
  excellent: "bg-emerald-50 text-emerald-700 border-emerald-200/60",
  very_good: "bg-blue-50 text-blue-700 border-blue-200/60",
  good: "bg-amber-50 text-amber-700 border-amber-200/60",
};

const conditionLabels = {
  excellent: "Excellent",
  very_good: "Very Good",
  good: "Good",
};

const tierLabels = {
  basic: "Basic",
  warranty: "With Extended Warranty",
};

const categoryLabels = {
  air: "MacBook Air",
  pro: "MacBook Pro",
};

export default function ProductCard({ macbook }) {
  const isSold = macbook.status === "sold";
  const getImageUrl = (url) => {
    if (!url) return null;
    return url;
  };

  return (
    <Link
      to={`/product/${macbook.id}`}
      data-testid={`product-card-${macbook.id}`}
      className={`product-card block rounded-2xl bg-white overflow-hidden ${isSold ? "sold-overlay" : ""}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-[#f6f5f1] overflow-hidden">
        {macbook.photos?.length > 0 ? (
          <img
            src={getImageUrl(macbook.photos[0])}
            alt={`${categoryLabels[macbook.category]} - ${tierLabels[macbook.tier]}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Laptop className="w-14 h-14 text-[#d4b978]" strokeWidth={1} />
          </div>
        )}

        {/* Sold Badge */}
        {isSold && (
          <div
            data-testid={`sold-badge-${macbook.id}`}
            className="absolute inset-0 flex items-center justify-center bg-black/20"
          >
            <span className="px-6 py-2 bg-white text-[#0a0a0a] text-sm font-bold rounded-full tracking-widest shadow-lg">
              SOLD
            </span>
          </div>
        )}

        {/* Condition Badge */}
        {!isSold && (
          <div className="absolute top-3 left-3">
            <Badge
              data-testid={`condition-badge-${macbook.id}`}
              className={`text-[11px] font-medium border ${conditionStyles[macbook.condition] || "bg-gray-50 text-gray-700 border-gray-200"}`}
            >
              {conditionLabels[macbook.condition] || macbook.condition}
            </Badge>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] font-semibold text-[#b5954a] uppercase tracking-wider">
            {categoryLabels[macbook.category] || macbook.category}
          </span>
          <span className="text-[10px] text-[#d4b978]">|</span>
          <span className="text-[11px] font-medium text-[#9a9a9a]">
            {tierLabels[macbook.tier] || macbook.tier}
          </span>
        </div>

        {macbook.short_description && (
          <p className="text-sm text-[#6b6b6b] mb-3 line-clamp-2 font-light">
            {macbook.short_description}
          </p>
        )}

        <div className="flex items-center justify-between">
          {!isSold ? (
            <>
              <span data-testid={`price-${macbook.id}`} className="text-xl font-bold text-[#0a0a0a]">
                ${macbook.price?.toLocaleString()}
              </span>
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200/60">
                Available
              </span>
            </>
          ) : (
            <span className="text-sm font-medium text-[#9a9a9a]">
              This MacBook has been sold
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
